import RoomIcon from '@mui/icons-material/Room'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import { styled, useTheme } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import Typography from '@mui/material/Typography'
import React from 'react'
import { Cog, Home } from '@styled-icons/heroicons-outline'
// eslint-disable-next-line import/no-extraneous-dependencies
import { StyledIconBase } from '@styled-icons/styled-icon'
import { Calendar } from '@styled-icons/heroicons-outline/Calendar'
import { CheckCircle } from '@styled-icons/heroicons-solid/CheckCircle'
import { Refresh } from '@styled-icons/heroicons-outline/Refresh'
import { Tag } from '@styled-icons/heroicons-outline/Tag'
import { DocumentText } from '@styled-icons/heroicons-outline/DocumentText'
import { ReactComponent as Swap } from 'assets/services/actions/Swap.svg'
import { ReactComponent as Frame } from 'assets/route_manager/Frame.svg'
import { ReactComponent as Material } from 'assets/route_manager/Material.svg'
import { ReactComponent as BusinessLine } from 'assets/route_manager/BusinessLine.svg'
import { ReactComponent as RouteIcon } from 'assets/Route.svg'

import { cyan } from '@mui/material/colors'
import T from 'T'
import MeasureChip from 'containers/new-route-manager/RouteCard/MeasureChip'
import { RECURRENCE, SERVICE_ACTION_TYPE } from 'settings/constants/service'
import { isValidHex } from 'utils/validations'
import TagChip from 'components/tags/Tag'
import { getWorkOrderDetailsPageUrl } from 'router/routes'
import RouteDetailsItem from './RouteDetailsItem'
import WorkOrderStatusChip from '../../../work-order/WorkOrderStatusChip'
import WeekDayIcon from './WeekDayIcon'
import ServiceActionTypeChip from '../../../services/ServiceActionTypeChip'
import RouteChip from '../work-orders/common/RouteChip'
import UnassignedChip from '../work-orders/common/UnassignedChip'

const Icon = styled(StyledIconBase)(({ theme, color = theme.palette.text.secondary, width = '12px', height = '12px' }) => ({
  width,
  height,
  color,
}))

const parseDaysOfWeek = (daysOfWeek: string) => {
  const dayAbbreviations = ['S', 'M', 'T', 'W', 'R', 'F', 'U']
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

  const result = dayAbbreviations.map((abbr, index) => ({
    day: dayNames[index],
    active: false,
  }))

  for (const abbreviation of daysOfWeek) {
    const index = dayAbbreviations.indexOf(abbreviation)
    if (index !== -1) {
      result[index].active = true
    }
  }

  return result
}

const { COLLECTION } = SERVICE_ACTION_TYPE
const { RECURRING } = RECURRENCE

type RouteTag = { tagName?: string; tagColor?: string }

type Props = {
  workOrderStatus?: string
  intentStatus?: string
  addressStr?: string
  daysOfWeek?: string
  businessLine?: string
  serviceType?: string
  serviceName?: string
  measure?: string
  materialType?: string
  materialColor?: string
  action?: string
  measureUnit?: string
  currentStop?: number
  stopCount?: number
  routeId?: string
  routeName?: string
  routeColor?: string
  serviceDate?: string
  sequence?: string | number
  recurrence?: string
  tags?: RouteTag[]
  workOrderNumber?: string
  workOrderId?: string
}

const RouteDetails: React.FC<Props> = ({
  workOrderStatus = '',
  intentStatus = '',
  addressStr = '',
  daysOfWeek = '',
  businessLine = '',
  serviceType = '',
  serviceName = '',
  measure = '',
  materialType = '',
  materialColor = '',
  action = '',
  measureUnit = '',
  currentStop = 0,
  stopCount = 0,
  routeId = '',
  routeName = '',
  routeColor = '',
  serviceDate = '',
  sequence = 0,
  recurrence = '',
  tags = [],
  workOrderNumber = '',
  workOrderId = '',
}) => {
  const theme = useTheme()
  const parsedDaysOfWeek = parseDaysOfWeek(daysOfWeek || '')
  const backgroundColor = isValidHex(materialColor) ? materialColor : '#000000'
  const color = theme.palette.getContrastText(backgroundColor)
  return (
    <Grid container rowSpacing={1} sx={{ width: '100%' }}>
      {addressStr && (
        <RouteDetailsItem icon={<RoomIcon sx={{ width: 14, height: 14, color: 'text.secondary' }} />} label="Location" value={addressStr} />
      )}
      <RouteDetailsItem
        icon={
          <SvgIcon fontSize="inherit" sx={{ color: 'text.secondary' }}>
            <Icon>
              <RouteIcon />
            </Icon>
          </SvgIcon>
        }
        label="Route"
        value={
          routeId ? (
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <RouteChip routeColor={routeColor} routeName={routeName} sequence={sequence} serviceDate={serviceDate} />
              <Typography variant="body1">
                {T.DRIVER_AT_STOP} {currentStop} of {stopCount}
              </Typography>
            </Stack>
          ) : (
            <UnassignedChip />
          )
        }
      />
      {(serviceName || serviceType) && (
        <RouteDetailsItem
          icon={
            <SvgIcon inheritViewBox viewBox="0 0 12 12" fontSize="inherit">
              <Icon>
                <Cog />
              </Icon>
            </SvgIcon>
          }
          label={T.SERVICE}
          value={serviceName || serviceType}
        />
      )}
      {recurrence && (
        <RouteDetailsItem
          icon={
            <SvgIcon inheritViewBox viewBox="0 0 12 12" fontSize="inherit">
              <Icon>
                <Refresh />
              </Icon>
            </SvgIcon>
          }
          label={T.RECURRENCE}
          value={recurrence}
        />
      )}
      {daysOfWeek && (
        <RouteDetailsItem
          icon={
            <SvgIcon inheritViewBox fontSize="inherit" sx={{ color: 'white' }} viewBox="0 0 12 12">
              <Icon>
                <Calendar />
              </Icon>
            </SvgIcon>
          }
          label={T.SCHEDULE}
          value={
            <Stack direction="row" spacing={0.5}>
              {parsedDaysOfWeek.map(({ day, active }) => {
                return <WeekDayIcon key={day} day={day} variant={active ? 'active' : 'outlined'} />
              })}
            </Stack>
          }
        />
      )}
      {businessLine && (
        <RouteDetailsItem
          icon={
            <SvgIcon inheritViewBox viewBox="0 0 12 12" fontSize="inherit">
              <Icon>
                <BusinessLine />
              </Icon>
            </SvgIcon>
          }
          label={T.BUSINESS_LINE}
          value={
            <Chip
              size="small"
              avatar={
                <Avatar sx={{ backgroundColor: `${theme.palette.background.black}61` }}>
                  <SvgIcon inheritViewBox viewBox="0 0 12 12" fontSize="inherit">
                    <Icon width="12px" height="12px" color={theme.palette.common.white}>
                      <Home />
                    </Icon>
                  </SvgIcon>
                </Avatar>
              }
              sx={{ backgroundColor: cyan[100] }}
              label={businessLine}
            />
          }
        />
      )}
      {workOrderStatus && (
        <RouteDetailsItem
          icon={
            <SvgIcon inheritViewBox viewBox="0 0 12 12" fontSize="inherit">
              <Icon>
                <CheckCircle />
              </Icon>
            </SvgIcon>
          }
          label="Service status"
          value={
            <Box display="flex" alignItems="center" gap={1}>
              <WorkOrderStatusChip intentStatus={intentStatus} status={workOrderStatus} />
            </Box>
          }
        />
      )}
      {(action || serviceType === RECURRING) && (
        <RouteDetailsItem
          icon={
            <SvgIcon inheritViewBox viewBox="0 0 12 12" fontSize="inherit">
              <Icon>
                <Swap />
              </Icon>
            </SvgIcon>
          }
          label="Action"
          value={
            <>
              {action && <ServiceActionTypeChip action={action} size="small" />}
              {!action && serviceType === RECURRING && (
                <ServiceActionTypeChip action={COLLECTION} label={T.RECURRING_COLLECTION} size="small" />
              )}
            </>
          }
        />
      )}
      {measure && (
        <RouteDetailsItem
          icon={
            <SvgIcon inheritViewBox viewBox="0 0 12 12" fontSize="inherit">
              <Icon>
                <Frame />
              </Icon>
            </SvgIcon>
          }
          label="Measure"
          value={<MeasureChip measureUnit={measureUnit} label={measure} />}
        />
      )}
      {materialType && (
        <RouteDetailsItem
          icon={
            <SvgIcon inheritViewBox viewBox="0 0 12 12" fontSize="inherit">
              <Icon>
                <Material />
              </Icon>
            </SvgIcon>
          }
          label={T.MATERIAL}
          value={<Chip label={materialType} size="small" sx={{ borderRadius: 1, backgroundColor, color }} />}
        />
      )}
      {tags && tags.length > 0 && (
        <RouteDetailsItem
          icon={
            <SvgIcon inheritViewBox viewBox="0 0 12 12" fontSize="inherit">
              <Icon>
                <Tag />
              </Icon>
            </SvgIcon>
          }
          label={T.TAGS}
          value={
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {tags.map(tag => (
                <TagChip key={`${tag.tagName}-${tag.tagColor}`} tag={tag} size="small" clickable={false} />
              ))}
            </Stack>
          }
        />
      )}
      {workOrderNumber && (
        <RouteDetailsItem
          icon={
            <SvgIcon inheritViewBox viewBox="0 0 12 12" fontSize="inherit">
              <Icon>
                <DocumentText />
              </Icon>
            </SvgIcon>
          }
          label={T.WORK_ORDER}
          value={
            workOrderId ? (
              <Link href={getWorkOrderDetailsPageUrl(workOrderId)} target="_blank" underline="hover">
                {workOrderNumber}
              </Link>
            ) : (
              workOrderNumber
            )
          }
        />
      )}
      <Grid size={12}>
        <Divider variant="fullWidth" flexItem />
      </Grid>
    </Grid>
  )
}

export default RouteDetails
